<template>
  <div>
    <!-- Load Study modal -->
    <div v-if="showLoadModal" class="fixed inset-0 z-40 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/30" @click="showLoadModal = false" />
      <div class="relative z-10 bg-white rounded-lg shadow-xl w-[56rem] max-w-[90vw] h-[80vh] flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span class="text-sm font-semibold text-gray-900">{{ kind === 'profile' ? 'Load Population' : 'Load Study' }}</span>
          <button @click="showLoadModal = false" class="text-gray-400 hover:text-gray-600">&times;</button>
        </div>
        <div class="overflow-y-auto flex-1">
          <div class="grid grid-cols-[minmax(0,1fr)_14rem_14rem] gap-x-4 px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200 sticky top-0 bg-white">
            <span>Name</span>
            <span>Template</span>
            <span>Population</span>
          </div>
          <div class="divide-y divide-gray-100">
            <div
              v-for="study in studies"
              :key="study.id"
              @click="pickStudy(study)"
              :class="[
                'grid grid-cols-[minmax(0,1fr)_14rem_14rem] gap-x-4 px-4 py-1.5 cursor-pointer items-baseline',
                selectedStudyId === study.id ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
            >
              <span class="text-xs text-gray-900 truncate">
                <svg class="inline w-3.5 h-3.5 mr-1.5 -mt-0.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>{{ study.name }}
              </span>
              <span class="text-xs font-mono text-gray-500 truncate">{{ study.template_key || '—' }}</span>
              <span class="text-xs text-gray-500 truncate">{{ study.population_name }}</span>
            </div>
          </div>
          <div v-if="!studies.length" class="px-4 py-8 text-center text-sm text-gray-400">No studies yet</div>
        </div>
      </div>
    </div>

    <!-- Study input picker modal -->
    <div v-if="daPicker" class="fixed inset-0 z-40 flex items-center justify-center">
      <div class="fixed inset-0 bg-black/30" @click="daPicker = null" />
      <div class="relative z-10 bg-white rounded-lg shadow-xl w-[56rem] max-w-[90vw] h-[80vh] flex flex-col">
        <div class="px-4 py-3 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <span class="text-sm font-semibold text-gray-900">{{ daPicker.title }}</span>
            <button class="text-gray-400 hover:text-gray-600" @click="daPicker = null">&times;</button>
          </div>
          <p class="text-xs text-gray-500 mt-0.5">Choose a saved Dataset or a direct DataAttribute.</p>
          <input
            ref="daSearchInput"
            v-model="daSearch"
            placeholder="Search inputs..."
            class="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded text-xs"
          />
        </div>
        <div class="overflow-y-auto flex-1">
          <div class="grid grid-cols-[8rem_minmax(0,1fr)_minmax(0,1.4fr)] gap-x-4 px-4 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200 sticky top-0 bg-white">
            <span>Kind</span><span>Name</span><span>Description</span>
          </div>
          <div class="divide-y divide-gray-100">
            <div
              v-for="option in inputPickerOptions"
              :key="option.source_type + '-' + option.id"
              class="grid grid-cols-[8rem_minmax(0,1fr)_minmax(0,1.4fr)] gap-x-4 px-4 py-1.5 cursor-pointer items-baseline hover:bg-gray-50"
              @click="pickInput(option)"
            >
              <span class="text-xs text-gray-500">{{ option.source_type === 'dataset' ? 'Dataset' : 'DataAttribute' }}</span>
              <span class="sensitive text-xs text-gray-900 whitespace-normal break-words">{{ option.name }}</span>
              <span class="sensitive text-xs text-gray-500 whitespace-normal break-words" data-demo-blur>{{ option.description || '' }}</span>
            </div>
          </div>
          <div v-if="!inputPickerOptions.length" class="px-4 py-8 text-center text-sm text-gray-400">No matches</div>
        </div>
      </div>
    </div>

    <!-- The study — script front and center, frame/params beside it -->
    <div class="space-y-4 min-w-0">
      <!-- Top bar -->
      <div class="flex items-center gap-3">
        <button class="btn-secondary-sm text-xs shrink-0 whitespace-nowrap" @click="openLoadModal">Load</button>
        <button class="btn-secondary-sm text-xs shrink-0 whitespace-nowrap" @click="newStudy">New study</button>
        <router-link
          :to="{ path: '/beta/agent', query: selectedStudyId ? { study: selectedStudyId } : {} }"
          class="btn-secondary-sm text-xs shrink-0 whitespace-nowrap"
        >Open in the agent</router-link>
        <input
          v-model="config.name"
          type="text"
          placeholder="Study name..."
          class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm font-medium"
        />
        <span class="text-xs font-mono px-2 py-1 bg-gray-900 text-gray-100 rounded">R</span>
        <span v-if="config.template_key" class="text-xs font-mono px-2 py-1 bg-gray-100 text-gray-700 rounded">{{ config.template_key }}</span>
        <span v-if="config.is_builtin" class="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">Built-in</span>
        <span v-if="saveError" class="text-xs text-red-600 max-w-xs truncate" :title="saveError">{{ saveError }}</span>
        <span v-if="hasAnyErrors" class="text-xs text-red-600">Validation errors</span>
        <button class="btn-secondary-sm text-xs whitespace-nowrap" :disabled="saving || !config.name" @click="saveStudy">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
        <button
          :disabled="!canRun || running || hasAnyErrors"
          class="btn-primary-sm text-xs whitespace-nowrap"
          @click="runStudy"
        >
          {{ running ? runPhase : 'Run Study' }}
        </button>
      </div>

      <div class="flex gap-4 items-start">
        <!-- Script: the study itself -->
        <div class="flex-1 min-w-0">
          <div class="border border-gray-300 rounded overflow-hidden">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-900">
              <span class="text-xs font-mono text-gray-400">script.R</span>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500 hidden lg:inline">ks_dataset() &middot; ks_frame() &middot; ks_params() &middot; ks_effect() &middot; ks_plot()</span>
                <select v-model="selectedTemplate" class="px-2 py-0.5 border border-gray-700 bg-gray-800 text-gray-200 rounded text-xs">
                  <option value="">Templates...</option>
                  <option v-for="t in templates" :key="t.key" :value="t.key">{{ t.key }}</option>
                </select>
                <button @click="applyTemplate" :disabled="!selectedTemplate" class="text-xs px-2 py-0.5 bg-primary-500 hover:bg-primary-600 text-white rounded">Apply</button>
              </div>
            </div>
            <textarea
              v-model="config.script"
              rows="30"
              spellcheck="false"
              class="w-full px-4 py-3 bg-gray-900 text-gray-100 text-xs font-mono leading-relaxed focus:outline-none resize-y"
              placeholder="# The study's R script.
# Reads saved Datasets via ks_dataset() and direct DataAttributes via ks_frame().
# Emits results via ks_effect() / ks_table() / ks_plot() / ks_diagnostic().
# Pick a template above to start."
            ></textarea>
          </div>
        </div>

        <!-- Frame & Params: what the script consumes -->
        <div class="w-80 shrink-0 space-y-3">
          <div v-if="!frameSpecs.length && !paramRows.length" class="border border-gray-200 rounded p-3 text-xs text-gray-400">
            The script defines what appears here — its ks_frame() declarations and its p &lt;- list(...) block generate the Frame and Params panels, and edits here rewrite the script.
          </div>
          <div v-for="spec in frameSpecs" :key="spec.source_type + '-' + spec.slug" class="border border-gray-200 rounded p-3 space-y-1.5">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-semibold text-gray-700">{{ spec.slug }}.csv</span>
              <span class="text-[10px] uppercase text-gray-400">{{ spec.source_type === 'dataset' ? 'Dataset' : 'DataAttribute' }}</span>
            </div>
            <button
              class="w-full px-1.5 py-1 border border-gray-300 rounded text-[11px] text-left whitespace-normal break-words bg-white hover:border-gray-400"
              :class="spec.found ? 'text-gray-900' : 'border-red-300 bg-red-50 text-red-700'"
              :title="spec.found ? spec.source_name : `no ${spec.source_type} matches '${spec.slug}'`"
              @click="openInputPicker(spec)"
            >{{ spec.source_name || spec.slug }}</button>
            <div v-if="spec.columns.length" class="flex flex-wrap gap-1">
              <code v-for="col in spec.columns" :key="col" class="text-[10px] text-gray-500">{{ col }}</code>
            </div>
          </div>

          <div v-if="paramRows.length" class="border border-gray-200 rounded p-3 space-y-3">
            <div class="text-xs font-semibold text-gray-700">Params <span class="font-normal text-gray-400">&middot; the script's p &lt;- list(...)</span></div>
            <div v-for="param in paramRows" :key="param.name" class="flex items-center gap-2">
              <code class="text-[11px] text-gray-700 w-28 shrink-0 truncate" :title="'p$' + param.name">{{ param.name }}</code>
              <input
                v-if="param.raw === 'TRUE' || param.raw === 'FALSE'"
                type="checkbox"
                :checked="param.raw === 'TRUE'"
                @change="setParamValue(param.name, $event.target.checked ? 'TRUE' : 'FALSE')"
                class="rounded"
              />
              <input
                v-else
                :value="param.raw"
                @change="setParamValue(param.name, $event.target.value)"
                class="flex-1 min-w-0 px-1.5 py-1 border border-gray-300 rounded text-[11px] font-mono"
                title="an R literal: 5, &quot;text&quot;, TRUE, c(age = 5, ...)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div v-if="results" class="border border-gray-200 rounded p-4 space-y-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">Results</span>
          <div class="flex items-center gap-2">
            <select
              v-if="studyRuns.length"
              :value="results.id"
              @change="loadRun(parseInt($event.target.value))"
              class="px-1.5 py-0.5 border border-gray-300 rounded text-[11px]"
            >
              <option v-for="(run, i) in studyRuns" :key="run.id" :value="run.id">
                #{{ studyRuns.length - i }} · {{ formatDate(run.run_at) }} · {{ run.status }}
              </option>
            </select>
            <span class="text-xs text-gray-500">{{ results.status }}</span>
          </div>
        </div>

        <div v-if="results.status === 'failed'" class="text-sm text-red-600 whitespace-pre-wrap">{{ results.error_message }}</div>
        <pre v-if="['pending', 'running'].includes(results.status)" class="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto max-h-40">{{ results.console_output || 'queued — waiting for a worker...' }}</pre>

        <!-- The run's outputs, in emission order: list -> detail -->
        <div v-if="runOutputs.length" class="flex gap-4 items-start">
          <div class="w-72 shrink-0 border border-gray-200 rounded divide-y divide-gray-100 overflow-y-auto max-h-[32rem]">
            <div
              v-for="(o, i) in runOutputs"
              :key="i"
              @click="selectedOutput = i"
              :class="[
                'px-3 py-1.5 cursor-pointer grid grid-cols-[1.2rem_4rem_minmax(0,1fr)] gap-x-2 items-baseline',
                selectedOutput === i ? 'bg-blue-50' : 'hover:bg-gray-50'
              ]"
            >
              <span class="text-[10px] font-mono text-gray-400">{{ i + 1 }}</span>
              <span class="text-[10px] font-mono uppercase text-gray-400">{{ o.kind }}</span>
              <span class="text-xs text-gray-900 truncate">{{ o.name }}</span>
            </div>
          </div>

          <div class="flex-1 min-w-0 border border-gray-200 rounded p-4" v-if="currentOutput">
            <template v-if="currentOutput.kind === 'effect'">
              <div class="text-xs font-medium text-gray-700 mb-2">{{ currentOutput.name }}</div>
              <pre class="text-xs font-mono text-gray-900">estimate  {{ currentOutput.estimate }}
ci_low    {{ currentOutput.ci_low }}
ci_high   {{ currentOutput.ci_high }}
p         {{ currentOutput.p }}
unit      {{ currentOutput.unit }}
model     {{ currentOutput.model }}</pre>
            </template>

            <template v-else-if="currentOutput.kind === 'plot'">
              <div class="text-xs font-medium text-gray-700 mb-2">{{ currentOutput.name }}</div>
              <img v-if="plotUrls[plotKey(currentOutput)]" :src="plotUrls[plotKey(currentOutput)]" :alt="currentOutput.name" class="w-full" />
              <div v-else class="text-xs text-gray-400">Loading plot…</div>
              <div v-if="currentOutput.caption" class="text-xs text-gray-400 mt-2">{{ currentOutput.caption }}</div>
            </template>

            <template v-else-if="currentOutput.kind === 'table'">
              <div class="text-xs font-medium text-gray-700 mb-2">{{ currentOutput.name }}</div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-xs border border-gray-200">
                  <thead>
                    <tr class="bg-gray-50">
                      <th v-for="c in currentOutput.columns" :key="c" class="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{{ c }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in currentOutput.rows" :key="i" class="border-b border-gray-100">
                      <td v-for="c in currentOutput.columns" :key="c" class="px-2 py-1">{{ row[c] }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="currentOutput.caption" class="text-xs text-gray-400 mt-2">{{ currentOutput.caption }}</div>
            </template>

            <template v-else>
              <div class="text-xs font-medium text-gray-700 mb-1">{{ currentOutput.name }}</div>
              <p class="text-sm text-gray-700">{{ currentOutput.detail }}</p>
            </template>
          </div>
        </div>

        <!-- Console -->
        <details v-if="results.console_output">
          <summary class="text-xs text-gray-500 cursor-pointer">Console output</summary>
          <pre class="sensitive text-xs bg-gray-900 text-gray-100 rounded p-3 mt-1 overflow-x-auto max-h-64" data-demo-blur>{{ results.console_output }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'StudyBuilder',
  props: {
    attributes: {
      type: Array,
      default: () => []
    },
    datasets: {
      type: Array,
      default: () => []
    },
    kind: {
      type: String,
      default: 'study'
    }
  },
  data() {
    return {
      studies: [],
      templates: [],
      selectedTemplate: '',
      selectedStudyId: null,
      loadedScript: '',
      loading: true,
      running: false,
      runPhase: 'Running...',
      saving: false,
      saveError: null,
      showLoadModal: false,
      frameSpecs: [],
      frameSpecTimer: null,
      daPicker: null,
      daSearch: '',

      results: null,
      studyRuns: [],
      plotUrls: {},
      selectedOutput: 0,
      pollTimer: null,
      config: this.emptyConfig(),
    };
  },
  computed: {
    canRun() {
      return Boolean(this.config.script);
    },
    hasAnyErrors() {
      return this.frameSpecs.some(s => !s.found);
    },
    runOutputs() {
      return this.results?.results?.outputs || [];
    },
    currentOutput() {
      return this.runOutputs[this.selectedOutput] || null;
    },
    inputPickerOptions() {
      if (!this.daPicker) return [];
      const options = [
        ...this.datasets.map((dataset) => ({ ...dataset, source_type: 'dataset' })),
        ...this.attributes.map((attribute) => ({ ...attribute, source_type: 'attribute' })),
      ];
      const q = this.daSearch.trim().toLowerCase();
      if (!q) return options;
      return options.filter(option =>
        option.name.toLowerCase().includes(q) ||
        (option.description || '').toLowerCase().includes(q));
    },
    paramRows() {
      const script = this.config.script || '';
      const m = /\bp\s*<-\s*list\s*\(/g.exec(script);
      if (!m) return [];
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return [];
      const body = script.slice(m.index + span.start, m.index + span.end);
      const rows = [];
      const re = /([\w.]+)\s*=\s*("(?:[^"\\]|\\.)*"|c\([^)]*\)|[^,\n)]+)/g;
      let am;
      while ((am = re.exec(body))) {
        rows.push({ name: am[1], raw: am[2].trim() });
      }
      return rows;
    },
  },
  watch: {
    'config.script'() {
      if (this.frameSpecTimer) clearTimeout(this.frameSpecTimer);
      this.frameSpecTimer = setTimeout(this.fetchFrameSpecs, 600);
    },
  },
  methods: {
    emptyConfig() {
      return {
        name: '',
        is_builtin: false,
        script: '',
        template_key: '',
      };
    },
    savePayload() {
      return {
        name: this.config.name,
        kind: this.kind,
        script: this.config.script,
        script_language: 'r',
        template_key: this.config.template_key,
      };
    },
    async saveStudy() {
      this.saving = true;
      this.saveError = null;
      try {
        let response;
        if (this.selectedStudyId) {
          response = await axios.put(`api/impact_analytics/studies/${this.selectedStudyId}/`, this.savePayload());
        } else {
          response = await axios.post('api/impact_analytics/studies/', this.savePayload());
          this.selectedStudyId = response.data.id;
        }
        this.loadedScript = this.config.script;
        await this.fetchStudies();
        return true;
      } catch (error) {
        const detail = error.response?.data;
        this.saveError = typeof detail === 'object'
          ? Object.entries(detail).map(([k, v]) => `${k}: ${v}`).join('; ').slice(0, 200)
          : 'Save failed';
        return false;
      } finally {
        this.saving = false;
      }
    },
    async fetchTemplates() {
      try {
        const response = await axios.get('api/impact_analytics/study-templates/');
        this.templates = response.data.filter((t) => (t.kind || 'study') === this.kind);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    },
    applyFlagshipTemplate() {
      const preferred = this.kind === 'profile' ? 'population_profile' : 'matched_did_aligned';
      const t = this.templates.find(x => x.key === preferred) || this.templates[0];
      if (!t) return;
      this.config.script = t.script;
      this.config.template_key = t.key;
    },
    applyTemplate() {
      const t = this.templates.find(x => x.key === this.selectedTemplate);
      if (!t) return;
      if (this.config.script && this.config.script !== t.script) {
        const current = this.templates.find(x => x.key === this.config.template_key);
        const edited = !current || this.config.script !== current.script;
        if (edited && !confirm(`Replace the current script with the ${t.key} template? Unsaved edits to the script will be lost.`)) {
          return;
        }
      }
      this.config.script = t.script;
      this.config.template_key = t.key;
    },
    plotKey(output) {
      return `${this.results && this.results.id}:${output.file}`;
    },
    async fetchPlots() {
      if (!this.results || !this.results.results) return;
      const outputs = this.results.results.outputs || [];
      for (const output of outputs.filter((o) => o.kind === 'plot')) {
        const key = this.plotKey(output);
        if (this.plotUrls[key]) continue;
        const slug = (output.file || '').replace(/^plots\//, '').replace(/\.png$/, '');
        try {
          const response = await axios.get(
            `api/impact_analytics/study-runs/${this.results.id}/artifact/`,
            { params: { name: slug }, responseType: 'blob' }
          );
          this.plotUrls[key] = URL.createObjectURL(response.data);
        } catch (error) {
          console.error('Error fetching plot:', slug, error);
        }
      }
    },
    pollRun(runId) {
      this.pollTimer = setInterval(async () => {
        try {
          const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
          this.results = response.data;
          if (this.selectedOutput >= (response.data.results?.outputs || []).length) {
            this.selectedOutput = 0;
          }
          if (['completed', 'failed'].includes(response.data.status)) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.running = false;
            this.fetchPlots();
            if (this.selectedStudyId) this.fetchStudyRuns(this.selectedStudyId);
          }
        } catch (error) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.running = false;
        }
      }, 2500);
    },
    async fetchStudies() {
      try {
        const response = await axios.get('api/impact_analytics/studies/', { params: { kind: this.kind } });
        this.studies = response.data;
      } catch (error) {
        console.error('Error fetching studies:', error);
      }
    },
    newStudy() {
      this.selectedStudyId = null;
      this.config = this.emptyConfig();
      this.paramsError = null;
      this.results = null;
      this.applyFlagshipTemplate();
    },
    openLoadModal() {
      this.fetchStudies();
      this.showLoadModal = true;
    },
    pickStudy(study) {
      this.showLoadModal = false;
      this.loadStudy(study);
    },
    async loadStudy(study) {
      this.selectedStudyId = study.id;
      this.results = null;
      this.studyRuns = [];
      this.fetchStudyRuns(study.id, true);

      // Fetch full study details
      try {
        const response = await axios.get(`api/impact_analytics/studies/${study.id}/`);
        this.loadedScript = response.data.script;
        const s = response.data;

        this.config = {
          name: s.name,
          is_builtin: s.is_builtin,
          script: s.script || '',
          template_key: s.template_key || '',
        };

        this.fetchFrameSpecs();
      } catch (error) {
        console.error('Error loading study details:', error);
      }
    },
    openInputPicker(spec) {
      this.daPicker = {
        title: `Input ${spec.slug}.csv`,
        slug: spec.slug,
        source_type: spec.source_type,
      };
      this.daSearch = '';
      this.$nextTick(() => this.$refs.daSearchInput?.focus());
    },
    pickInput(option) {
      const newSlug = this.slugifyName(option.name);
      this.setInput(this.daPicker, option.source_type, newSlug);
      this.daPicker = null;
    },
    slugifyName(name) {
      const slug = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      return /^[0-9]/.test(slug) ? `x${slug}` : slug;
    },
    setInput(current, sourceType, newSlug) {
      const currentFunction = current.source_type === 'dataset' ? 'ks_dataset' : 'ks_frame';
      const nextFunction = sourceType === 'dataset' ? 'ks_dataset' : 'ks_frame';
      const pattern = new RegExp(`(${currentFunction}\\(\\s*")${current.slug}(")`);
      this.config.script = this.config.script.replace(
        pattern,
        `${nextFunction}("${newSlug}"`,
      );
    },
    findCallSpan(script, headRe) {
      const m = headRe.exec(script);
      if (!m) return null;
      let i = m.index + m[0].length;
      let depth = 1;
      let quote = null;
      const start = i;
      while (i < script.length && depth) {
        const ch = script[i];
        if (quote) {
          if (ch === quote && script[i - 1] !== '\\') quote = null;
        } else if (ch === '"' || ch === "'") {
          quote = ch;
        } else if (ch === '(') {
          depth += 1;
        } else if (ch === ')') {
          depth -= 1;
        }
        i += 1;
      }
      return depth === 0 ? { start, end: i - 1 } : null;
    },
    setParamValue(name, raw) {
      const script = this.config.script;
      const head = /\bp\s*<-\s*list\s*\(/g;
      const m = head.exec(script);
      if (!m) return;
      const span = this.findCallSpan(script.slice(m.index), /^p\s*<-\s*list\s*\(/);
      if (!span) return;
      const abs = { start: m.index + span.start, end: m.index + span.end };
      const body = script.slice(abs.start, abs.end);
      const re = new RegExp(`(\\b${name}\\s*=\\s*)("(?:[^"\\\\]|\\\\.)*"|c\\([^)]*\\)|[^,\\n)]+)`);
      if (!re.test(body)) return;
      const newBody = body.replace(re, `$1${raw}`);
      this.config.script = script.slice(0, abs.start) + newBody + script.slice(abs.end);
    },
    async fetchStudyRuns(studyId, autoloadLatest = false) {
      try {
        const response = await axios.get(`api/impact_analytics/studies/${studyId}/runs/`);
        this.studyRuns = response.data;
        if (autoloadLatest && this.studyRuns.length) {
          this.loadRun(this.studyRuns[0].id);
        }
      } catch (error) {
        console.error('Error fetching study runs:', error);
      }
    },
    async loadRun(runId) {
      try {
        const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
        this.results = response.data;
        this.selectedOutput = 0;
        this.fetchPlots();
      } catch (error) {
        console.error('Error loading run:', error);
      }
    },
    async fetchFrameSpecs() {
      if (!this.config.script) {
        this.frameSpecs = [];
        return;
      }
      try {
        const response = await axios.post('api/impact_analytics/study-frame-spec/', {
          script: this.config.script,
        });
        this.frameSpecs = response.data;
      } catch (error) {
        console.error('Error resolving frame spec:', error);
      }
    },
    getAttributeName(id) {
      const attr = this.attributes.find(a => a.id === id);
      return attr ? attr.name : '';
    },
    async runStudy() {
      if (!this.canRun || this.hasAnyErrors) return;
      this.running = true;
      this.runPhase = 'Saving...';
      this.results = null;

      if (this.selectedStudyId && this.config.script === this.loadedScript) {
        // Not edited locally: adopt the server's current script instead of
        // overwriting it with a possibly stale browser copy.
        try {
          const detail = await axios.get(`api/impact_analytics/studies/${this.selectedStudyId}/`);
          this.config.script = detail.data.script;
          this.loadedScript = detail.data.script;
        } catch (error) {
          console.error('Error refreshing study script:', error);
        }
      } else {
        const saved = await this.saveStudy();
        if (!saved) {
          this.running = false;
          return;
        }
      }

      this.runPhase = 'Running...';
      try {
        const response = await axios.post(`api/impact_analytics/studies/${this.selectedStudyId}/run/`, {});
        this.results = response.data;
        if (['pending', 'running'].includes(response.data.status)) {
          this.pollRun(response.data.id);
        } else {
          this.running = false;
        }
      } catch (error) {
        console.error('Error running study:', error);
        this.saveError = error.response?.data?.error || error.message;
        this.running = false;
      }
    },
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleString();
    },
  },
  mounted() {
    this.fetchStudies();
    this.fetchTemplates();
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
};
</script>
